import React, { useEffect, useState } from "react";
import Header from "./Header";
import { authHeader } from "./helpers/auth-header";
import { useNavigate } from "react-router-dom"; // add
import { BS_BASE } from "./configs/api";

function formatDateYMDToDMY(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
}

function typeLabel(type) {
  // Adjust mapping as needed (example: 1 = Lesson, 2 = School)
  if (type === 2) return "School booking";
  return "Lesson booking";
}

function ConfirmBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [actingId, setActingId] = useState(null);
  const navigate = useNavigate(); // add

  const fetchUnconfirmed = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${BS_BASE}/get-unconfirmed-slots`,
        {
          headers: { Accept: "application/json", ...authHeader() }
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];

      // Only unconfirmed; normalize fields
      const normalized = list
        .filter((b) => b && (b.accepted === false || b.accepted === 0 || b.accepted === "false"))
        .map((b) => ({
          id: b.id,
          date: b.date,           // "2025-09-10"
          day: b.day,             // "Wednesday"
          start: String(b.startTime || "").slice(0, 5), // "HH:mm"
          end: String(b.endTime || "").slice(0, 5),     // "HH:mm"
          type: b.type,           // number
          instructorId: b.instructorId,
          firstName: b.firstName,
          lastName: b.lastName,
        }));

      setBookings(normalized);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnconfirmed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirmAll = async () => {
    if (!bookings.length) return;
    setConfirming(true);
    setError("");
    try {
      const res = await fetch(
        `${BS_BASE}/accept-booking`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeader(),
          },
          body: JSON.stringify(bookings.map(b => b.id) )
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchUnconfirmed();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setConfirming(false);
    }
  };

  const handleConfirmOne = async (id) => {
    setActingId(id);
    setError("");
    try {
      const res = await fetch(`${BS_BASE}/accept-booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify([id])
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchUnconfirmed();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setActingId(null);
    }
  };

  const handleRejectOne = async (id) => {
    setActingId(id);
    setError("");
    try {
      const res = await fetch(`${BS_BASE}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify([id])
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchUnconfirmed();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="bookingform-page" style={{ minHeight: "100vh" }}>
      <div className="bookingform-form main-container" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/* add back button */}
        <Header onBack={() => navigate(-1)} />
        <h2 style={{ margin: "6px 0 12px 0" }}>
          {loading ? "Loading…" : "Confirmed Bookings"}
        </h2>

        <div style={{ width: "100%", display: "flex", justifyContent: "center", margin: "2px 0 8px" }}>
          <button
            onClick={handleConfirmAll}
            disabled={loading || confirming || bookings.length === 0}
            style={{
              background: "#1976d2",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "8px 16px",
              fontWeight: 700,
              cursor: loading || confirming || bookings.length === 0 ? "not-allowed" : "pointer",
              opacity: loading || confirming || bookings.length === 0 ? 0.6 : 1
            }}
          >
            {confirming ? "Confirming…" : "Confirm All"}
          </button>
        </div>

        {error && (
          <div style={{ color: "crimson", fontSize: "0.9rem", textAlign: "center", marginBottom: 8 }}>
            Error: {error}
          </div>
        )}

        {!loading && bookings.length === 0 && !error && (
          <div style={{ color: "#666", marginTop: 8 }}>No unconfirmed bookings.</div>
        )}

        {!loading && bookings.length > 0 && (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {bookings.map((b) => (
              <li
                key={b.id}
                style={{
                  border: "1px solid #e2e2e2",
                  borderRadius: 8,
                  padding: "10px 12px",
                  background: "#fff",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <strong>
                    {b.day || ""} • {formatDateYMDToDMY(b.date)} <br />• {b.start}
                    {b.end ? `–${b.end}` : ""}
                  </strong>
                  <span style={{ color: "#777", fontSize: "0.9rem" }}>
                    {b.firstName} {b.lastName}
                  </span>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => handleConfirmOne(b.id)}
                    disabled={actingId === b.id || confirming || loading}
                    style={{
                      background: "#2e7d32",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      padding: "6px 12px",
                      fontWeight: 700,
                      cursor: actingId === b.id || confirming || loading ? "not-allowed" : "pointer",
                      opacity: actingId === b.id || confirming || loading ? 0.6 : 1
                    }}
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => handleRejectOne(b.id)}
                    disabled={actingId === b.id || confirming || loading}
                    style={{
                      background: "#c62828",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      padding: "6px 12px",
                      fontWeight: 700,
                      cursor: actingId === b.id || confirming || loading ? "not-allowed" : "pointer",
                      opacity: actingId === b.id || confirming || loading ? 0.6 : 1
                    }}
                  >
                    Reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default ConfirmBookings;